import React, { useState, useMemo, useEffect } from 'react';
import {
  Check,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  AlertCircle,
  LogOut,
} from 'lucide-react';
import { User, db, doc, setDoc, addDoc, collection, logout } from '../firebase';
import { useAppSettings } from '../hooks/useAppSettings';
import { cn } from '../lib/utils';
import { CapacitorHttp } from '@capacitor/core';
import { Stripe, GooglePayEventsEnum } from '@capacitor-community/stripe';

interface CapacitorPaywallScreenProps {
  user: User;
  theme: 'light' | 'dark' | 'system';
}

export function CapacitorPaywallScreen({
  user,
  theme,
}: CapacitorPaywallScreenProps) {
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const config = useAppSettings();

  const isDark = useMemo(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme === 'dark';
  }, [theme]);

  // Google Pay brand guidelines: Use white button on dark backgrounds, black on light.
  const buttonColor = isDark ? 'white' : 'black';

  useEffect(() => {
    if (config.stripePublishableKey) {
      Stripe.initialize({
        publishableKey: config.stripePublishableKey.trim(),
      });
    }
  }, [config.stripePublishableKey]);

  const handleNativePayment = async () => {
    if (!config.stripePublishableKey) return;

    setLoading(true);
    setError(null);

    try {
      // Ensure Stripe is initialized before any payment call to avoid "apiKey not provided" errors
      await Stripe.initialize({
        publishableKey: config.stripePublishableKey.trim(),
      });

      // Ensure Google Pay is available on this device
      await Stripe.isGooglePayAvailable();

      // 1. Create a PaymentIntent on your backend.
      // We use CapacitorHttp to bypass CORS issues on native devices
      const response = await CapacitorHttp.post({
        url: 'https://us-central1-kowope-finance.cloudfunctions.net/createStripePaymentIntent',
        headers: { 'Content-Type': 'application/json' },
        data: {
          amount: Math.round(Number(config.price) * 100), // Amount in cents
          currency: config.currencyCode.toLowerCase(),
          uid: user.uid,
        },
      });

      if (response.status !== 200) {
        throw new Error(
          response.data?.message || 'Failed to initialize payment intent.'
        );
      }

      const { clientSecret } = response.data;

      // 2. Prepare the native Google Pay sheet
      await Stripe.createGooglePay({
        paymentIntentClientSecret: clientSecret,
        merchantIdentifier: 'com.akandiecomputers.kowope',
        paymentSummaryItems: [
          {
            label: 'Kówópé Monthly Subscription',
            amount: Number(config.price),
          },
        ],
        countryCode: String(config.countryCode),
        currency: String(config.currencyCode),
      });

      // 3. Present the Google Pay sheet
      const result = await Stripe.presentGooglePay();

      if (result.paymentResult === GooglePayEventsEnum.Completed) {
        // 4. Update Firestore after successful payment confirmation
        await setDoc(
          doc(db, 'users', user.uid),
          {
            hasPaid: true,
            paymentDate: new Date().toISOString(),
            plan: 'monthly_subscription',
            paymentMethod: 'google_pay_native',
            subscriptionStatus: 'active',
          },
          { merge: true }
        );

        // Record transaction in history
        await addDoc(collection(db, 'users', user.uid, 'app_payments'), {
          date: new Date().toISOString(),
          amount: Number(config.price),
          currency: config.currencyCode,
          status: 'success',
          method: 'google_pay_native',
        });

        setIsSuccess(true);
      }
    } catch (e: unknown) {
      console.error('Native payment failed', e);
      setError(
        e instanceof Error ? e.message : 'Payment failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-emerald-50 p-6 font-sans transition-colors duration-300 dark:bg-black">
        <div className="w-full max-w-md space-y-6 rounded-[40px] border border-emerald-100 bg-white p-10 text-center shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500 shadow-xl shadow-emerald-200 dark:shadow-emerald-900/20">
            <CheckCircle2 className="h-12 w-12 text-white" />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Payment Successful!
            </h1>
            <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              Thank you for subscribing. Your premium features are now unlocked.
            </p>
          </div>

          <button
            onClick={() => window.location.reload()} // Reload to ensure app state refreshes
            className="w-full rounded-2xl bg-zinc-900 py-4 font-bold text-white shadow-lg transition-opacity hover:opacity-90 active:scale-95 dark:bg-zinc-100 dark:text-zinc-900"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  if (!config.stripePublishableKey) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-6 transition-colors duration-300 dark:bg-black">
      <div className="w-full max-w-md space-y-8 rounded-[40px] border border-zinc-100 bg-white p-8 text-center shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-indigo-600 shadow-xl shadow-indigo-200 dark:shadow-indigo-900/20">
          <ShieldCheck className="h-10 w-10 text-white" />
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
            Unlock Kówópé
          </h1>
          <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            Get complete control over your finances with our premium features.
          </p>
        </div>

        <div className="space-y-3 rounded-3xl border border-zinc-100 bg-zinc-50 p-6 text-left dark:border-zinc-800 dark:bg-zinc-950/50">
          {config.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-3">
              <Check className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {feature}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="mb-2">
            <span className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
              ${config.price}
            </span>
            <span className="text-sm font-medium text-zinc-500"> / month</span>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-left text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          <button
            onClick={handleNativePayment}
            disabled={loading}
            className={cn(
              'flex w-full items-center justify-center gap-3 rounded-2xl py-4 font-bold shadow-lg transition-all active:scale-95 disabled:opacity-50',
              buttonColor === 'white'
                ? 'border border-black bg-zinc-100 text-zinc-900'
                : 'border border-white bg-zinc-900 text-white'
            )}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <img
                src={
                  buttonColor === 'white'
                    ? 'https://www.gstatic.com/instantbuy/svg/light/subscribe/en.svg'
                    : 'https://www.gstatic.com/instantbuy/svg/dark/subscribe/en.svg'
                }
                alt="Subscribe with Google Pay"
                className="h-7 w-auto"
              />
            )}
          </button>

          <button
            onClick={() => logout()}
            className="mt-4 flex w-full items-center justify-center gap-2 py-2 text-xs font-bold text-zinc-400 transition-colors hover:text-rose-500"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>

          <p className="mt-4 text-[10px] text-zinc-400">
            Secured by Google Pay. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
